import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, where, Timestamp } from "firebase/firestore";
import { db } from '../config/firebase';
import { styles } from '../config/styles';
import { formOptions } from '../constants/formOptions';
import { formatCurrency, formatTasaForDisplay, formatNumberForDisplay, formatNumberForInput, parseAmountForState, parseTasaForState, formatComitenteForDisplay } from '../utils/formatters';
import { calculateVencimiento, formatHoraForDisplay } from '../utils/dateHelpers';
import { computeKpiTotales } from '../utils/planillaKpis';
import { vencimientosFinanciacionEnVivo } from '../utils/vencimientosFinanciacion';
import { MONEDA_BUCKETS, MONEDA_MOVIMIENTO, MONEDA_TITULO } from '../constants/monedas';
import { compareValues } from '../utils/sortHelpers';
import { useHorizontalScrollSync } from '../hooks/useHorizontalScrollSync';
import { useStackedTable } from '../hooks/useStackedTable';
import { useMobileCollapse } from '../hooks/useMobileCollapse';
import TopScrollbar from './TopScrollbar';
import CollapsibleHeader from './common/CollapsibleHeader';
import MobileCollapsibleSection from './common/MobileCollapsibleSection';
import { SortIcon, PencilIcon, TrashIcon } from './Icons';
import ParcialesModal from './ParcialesModal';
import VencimientosTab from './VencimientosTab';
import SaldoClienteBadge from './cauciones/SaldoClienteBadge';
import CierreHealthBanner from './cauciones/CierreHealthBanner';
import Modal from './common/Modal';
import { fieldStyles } from '../config/formStyles';
import OperacionBadge from './cauciones/OperacionBadge';
import { useSaldosClientes } from '../hooks/useSaldosClientes';
import CaucionFormModal from './CaucionFormModal';
import PasesPlanilla from './pases/PasesPlanilla';
import { softDeleteCaucion } from '../services/caucionesService';
import ImportarCaucionesModal from './cauciones/ImportarCaucionesModal';
import ConciliacionCaucionesPanel from './cauciones/ConciliacionCaucionesPanel';
import { subscribeByComitentes } from '../services/comitentesQueryService';
import { filterMisClientes } from '../utils/saldosScope';

// Sub-componente reutilizable para los indicadores
const IndicatorCard = ({ title, value, currency }) => (
  <div className="axon-kpi-card" style={styles.kpiCard}>
    <h3 style={styles.kpiTitle}>{title}</h3>
    <p className="axon-kpi-value" style={styles.kpiValue}>{formatCurrency(value, currency)}</p>
  </div>
);

// Estilos de la sub-navegación interna (Planilla de Cauciones / Vencimientos)
const subTabContainerStyle = {
  display: 'flex',
  gap: '0.5rem',
  borderBottom: `1px solid ${styles.colors.gray}`,
  marginBottom: '2rem',
};

const subTabButtonStyle = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: '1rem',
  fontWeight: 600,
  padding: '0.5rem 1rem',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: styles.colors.mediumGray,
  borderBottom: '3px solid transparent',
  marginBottom: '-1px',
};

const subTabButtonActiveStyle = {
  color: styles.colors.primary,
  borderBottom: `3px solid ${styles.colors.primary}`,
};

// Estilos de la sección de filtros (equivalentes a Operaciones e Instrucciones)
const filterContainerStyle = {
  marginBottom: '1.5rem',
  backgroundColor: '#FFFFFF',
  borderRadius: '0.5rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  borderLeft: `3px solid ${styles.colors.primary}`,
};

const filterHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.75rem 1.25rem',
  borderBottom: '1px solid #E5E7EB',
};

const filterHeaderLeftStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const filterTitleStyle = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: '0.85rem',
  fontWeight: '600',
  color: styles.colors.primary,
  margin: 0,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const filterBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: styles.colors.primary,
  color: '#FFFFFF',
  fontSize: '0.65rem',
  fontWeight: '700',
  borderRadius: '10px',
  minWidth: '18px',
  height: '18px',
  padding: '0 5px',
};

const filterGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: '0.75rem',
  padding: '1rem 1.25rem',
};

const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: '0.3rem' };

const filterLabelStyle = {
  fontSize: '0.68rem',
  fontWeight: '600',
  textTransform: 'uppercase',
  color: styles.colors.mediumGray,
  letterSpacing: '0.05em',
};

const filterInputStyle = {
  padding: '0.5rem 0.65rem',
  border: '1px solid #E5E7EB',
  borderRadius: '0.5rem',
  fontSize: '0.8rem',
  fontFamily: "'Roboto', sans-serif",
  boxSizing: 'border-box',
  width: '100%',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  outline: 'none',
  color: styles.colors.secondary,
};

const filterInputActiveStyle = {
  borderColor: styles.colors.primary,
  boxShadow: '0 0 0 2px rgba(26, 40, 62, 0.1)',
};

const filterSelectFieldStyle = {
  padding: '0.5rem 0.65rem',
  border: '1px solid #E5E7EB',
  borderRadius: '0.5rem',
  fontSize: '0.8rem',
  fontFamily: "'Roboto', sans-serif",
  backgroundColor: '#FFFFFF',
  boxSizing: 'border-box',
  width: '100%',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  outline: 'none',
  color: styles.colors.secondary,
  cursor: 'pointer',
};

const filterSelectFieldActiveStyle = {
  borderColor: styles.colors.primary,
  boxShadow: '0 0 0 2px rgba(26, 40, 62, 0.1)',
};

const clearFiltersButtonStyle = {
  padding: '0.4rem 0.85rem',
  border: 'none',
  borderRadius: '0.5rem',
  fontSize: '0.75rem',
  fontFamily: "'Roboto', sans-serif",
  backgroundColor: 'rgba(239, 68, 68, 0.1)',
  color: '#DC2626',
  cursor: 'pointer',
  fontWeight: '500',
  transition: 'background-color 0.2s',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
};

/*
 * comitentesFilter (opcional, modo productor): lista de nroComitente. Si se pasa,
 * cauciones, pases y vencimientos se suscriben acotados a esos comitentes (única
 * lectura que las reglas le permiten a un productor) y se ocultan las
 * herramientas de mesa (conciliación e importación de archivos de mercado).
 *
 * filtroMisClientes (opcional, rol Comercial): filtro de vista "Mis clientes" de
 * cauciones y pases, indicadores incluidos (ver saldosScope.filterMisClientes).
 */
const CaucionesTab = ({ userRole, userName, userId, clientesDb, comitentePorCuit, especies, onViewComment, onNavigateToSaldos, comitentesFilter = null, filtroMisClientes = null }) => {
  const { getSaldoComitente, clientesDeudoresSinPrecarga, tieneSnapshotInicialHoy } = useSaldosClientes();
  const esModoProductor = comitentesFilter !== null;
  const comitentesKey = esModoProductor ? [...comitentesFilter].sort().join('|') : null;

  const [cauciones, setCauciones] = useState([]);
  const [pases, setPases] = useState([]);
  const [isPasesLoading, setIsPasesLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFeedback, setImportFeedback] = useState(null);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [montoOperadoModalState, setMontoOperadoModalState] = useState({ isOpen: false, caucion: null });
  const [montoOperadoValue, setMontoOperadoValue] = useState('');
  const [tasaOperadaValue, setTasaOperadaValue] = useState('');
  const [parcialesModalState, setParcialesModalState] = useState({ isOpen: false, caucion: null });
  const [caucionToReject, setCaucionToReject] = useState(null);
  const [rejectionComment, setRejectionComment] = useState('');
  const [editingCaucion, setEditingCaucion] = useState(null);
  const [caucionPrefill, setCaucionPrefill] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'urgencia', direction: 'descending' });
  const [filters, setFilters] = useState({});
  const filtersPanel = useMobileCollapse();
  const stackedTable = useStackedTable({
    title: 'Cliente',
    primary: ['ID', 'Operación', 'Monto', 'Tasa', 'Días', 'Saldo Cliente', 'Estado', 'Parciales'],
    actions: 'Acciones',
  });
  const [editingTasaId, setEditingTasaId] = useState(null);
  const [editingTasaValue, setEditingTasaValue] = useState('');
  const [deudoresPanel, setDeudoresPanel] = useState({ isOpen: false, moneda: MONEDA_MOVIMIENTO.ARS });
  const [activeSubTab, setActiveSubTab] = useState('planilla');
  const [vencimientos, setVencimientos] = useState([]);
  const [isVencimientosLoading, setIsVencimientosLoading] = useState(true);

  const uniqueComerciales = useMemo(() => {
    const values = new Set(cauciones.map(c => c.comercial).filter(Boolean));
    return [...values].sort();
  }, [cauciones]);

  const uniqueOperadores = useMemo(() => {
    const values = new Set(cauciones.map(c => c.operador).filter(Boolean));
    return [...values].sort();
  }, [cauciones]);

  useEffect(() => {
    setIsLoading(true);
    if (comitentesKey !== null) {
      const comitentes = comitentesKey ? comitentesKey.split('|') : [];
      return subscribeByComitentes('cauciones', comitentes, (docs) => {
        setCauciones(docs.filter(c => c.estado !== 'Eliminada'));
        setIsLoading(false);
      }, () => setIsLoading(false));
    }
    const q = query(collection(db, "cauciones"), where("estado", "!=", "Eliminada"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const caucionesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCauciones(caucionesData);
      setIsLoading(false);
    }, (error) => {
        console.error("Error al obtener las cauciones:", error);
        setIsLoading(false);
    });
    return () => unsubscribe();
  }, [comitentesKey]);

  useEffect(() => {
    setIsVencimientosLoading(true);
    if (comitentesKey !== null) {
      const comitentes = comitentesKey ? comitentesKey.split('|') : [];
      return subscribeByComitentes('vencimientos', comitentes, (docs) => {
        setVencimientos(docs);
        setIsVencimientosLoading(false);
      }, () => setIsVencimientosLoading(false));
    }
    const q = query(collection(db, "vencimientos"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const vencimientosData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVencimientos(vencimientosData);
      setIsVencimientosLoading(false);
    }, (error) => {
      console.error("Error al obtener los vencimientos:", error);
      setIsVencimientosLoading(false);
    });
    return () => unsubscribe();
  }, [comitentesKey]);

  useEffect(() => {
    setIsPasesLoading(true);
    if (comitentesKey !== null) {
      const comitentes = comitentesKey ? comitentesKey.split('|') : [];
      return subscribeByComitentes('pases', comitentes, (docs) => {
        setPases(docs.filter(p => p.estado !== 'Eliminada'));
        setIsPasesLoading(false);
      }, () => setIsPasesLoading(false));
    }
    const q = query(collection(db, "pases"), where("estado", "!=", "Eliminada"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const pasesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPases(pasesData);
      setIsPasesLoading(false);
    }, (error) => {
      console.error("Error al obtener los pases:", error);
      setIsPasesLoading(false);
    });
    return () => unsubscribe();
  }, [comitentesKey]);

  // Vencimientos "en vivo": tomadoras Hechas (cauciones y pases) proyectadas
  // como vencimientos sin esperar al cierre nocturno, mergeadas con los docs ya
  // archivados en la colección `vencimientos` (ver vencimientosFinanciacionEnVivo).
  const vencimientosMerged = useMemo(() => [
    ...vencimientos,
    ...vencimientosFinanciacionEnVivo(cauciones, pases)
      .map(v => ({ ...v, fechaVencimiento: Timestamp.fromDate(v.fechaVencimiento) })),
  ], [vencimientos, cauciones, pases]);

  const kpiTotales = useMemo(
    () => computeKpiTotales(filterMisClientes(cauciones, filtroMisClientes)),
    [cauciones, filtroMisClientes],
  );

  const caucionesConId = useMemo(() => {
    const sortedByDate = [...cauciones].sort((a, b) => {
      const dateA = a.createdAt?.toDate() || 0;
      const dateB = b.createdAt?.toDate() || 0;
      return dateA - dateB;
    });
    return sortedByDate.map((caucion, index) => ({ ...caucion, persistentId: index + 1 }));
  }, [cauciones]);

  const urgenciaDeCaucion = useCallback((caucion) => {
    const saldo = getSaldoComitente(caucion.nroComitente, caucion.moneda);
    const proyectado = saldo?.saldoProyectado ?? 0;
    return caucion.operacion === 'Colocadora' ? proyectado : -proyectado;
  }, [getSaldoComitente]);

  const sortedCauciones = useMemo(() => {
    const sortableItems = [...caucionesConId];
    if (sortConfig.key === 'urgencia') {
      sortableItems.sort((a, b) => {
        const aPendiente = a.estado === 'Pendiente';
        const bPendiente = b.estado === 'Pendiente';
        if (aPendiente !== bPendiente) return aPendiente ? -1 : 1;
        if (aPendiente && bPendiente) {
          const diff = urgenciaDeCaucion(b) - urgenciaDeCaucion(a);
          if (diff !== 0) return diff;
        }
        const dateA = a.createdAt?.toDate?.()?.getTime?.() || 0;
        const dateB = b.createdAt?.toDate?.()?.getTime?.() || 0;
        return dateB - dateA;
      });
      return sortableItems;
    }
    sortableItems.sort((a, b) =>
      compareValues(a[sortConfig.key], b[sortConfig.key], sortConfig.direction)
    );
    return sortableItems;
  }, [caucionesConId, sortConfig, urgenciaDeCaucion]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => {
      if (!value) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);

  const filteredCauciones = useMemo(() => {
    const visibles = filterMisClientes(sortedCauciones, filtroMisClientes);
    if (!hasActiveFilters) return visibles;
    const cliente = filters.cliente?.trim().toLowerCase();
    const nroComitente = filters.nroComitente?.trim().toLowerCase();
    return visibles.filter(c => {
      if (cliente && !c.cliente?.toLowerCase().includes(cliente)) return false;
      if (nroComitente && !c.nroComitente?.toLowerCase().includes(nroComitente)) return false;
      if (filters.operacion && c.operacion !== filters.operacion) return false;
      if (filters.moneda && c.moneda !== filters.moneda) return false;
      if (filters.mercado && c.mercado !== filters.mercado) return false;
      if (filters.comercial && c.comercial !== filters.comercial) return false;
      if (filters.operador && c.operador !== filters.operador) return false;
      if (filters.estado && c.estado !== filters.estado) return false;
      return true;
    });
  }, [sortedCauciones, filtroMisClientes, filters, hasActiveFilters]);

  const { topRef, contentRef, contentWidth, onTopScroll, onContentScroll } =
    useHorizontalScrollSync([filteredCauciones, isLoading, activeSubTab]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Deudores sin línea precargada por moneda, indexados por la etiqueta con la
  // que la moneda se guarda en la caución ('Pesos' | 'USD Mep' | 'USD Cable').
  const deudoresPorMoneda = useMemo(
    () => MONEDA_BUCKETS.reduce((acc, bucket) => ({
      ...acc,
      [MONEDA_MOVIMIENTO[bucket]]: clientesDeudoresSinPrecarga(MONEDA_MOVIMIENTO[bucket]),
    }), {}),
    [clientesDeudoresSinPrecarga],
  );
  const monedasConDeudores = MONEDA_BUCKETS.filter(
    b => deudoresPorMoneda[MONEDA_MOVIMIENTO[b]].length > 0);
  const hayDeudores = monedasConDeudores.length > 0;

  const handleCrearParaDeudor = useCallback((deudor, moneda) => {
    setEditingCaucion(null);
    setCaucionPrefill({
      comercial: userRole === 'Comercial' ? userName : '',
      operacion: 'Tomadora',
      nroComitente: deudor.nroComitente,
      cliente: deudor.nombre || clientesDb?.[deudor.nroComitente] || '',
      moneda,
    });
    setDeudoresPanel({ isOpen: false, moneda });
    setIsModalOpen(true);
  }, [userRole, userName, clientesDb]);


  const handleDelete = async (caucion) => {
    if (!window.confirm("¿Está seguro de que desea eliminar esta caución?")) return;
    try {
      await softDeleteCaucion(caucion, { userId, userName });
    } catch (error) {
      console.error("Error al eliminar la caución:", error);
      alert("No se pudo eliminar la caución. Intentá de nuevo.");
    }
  };

  const handleStatusChange = async (caucion, newStatus) => {
    if (newStatus !== 'Pendiente' && (!caucion.monto || !caucion.dias)) {
        alert("No se puede cambiar el estado. La caución debe tener valores de Monto y Días definidos.");
        const selectElement = document.querySelector(`select[data-id="${caucion.id}"]`);
        if(selectElement) selectElement.value = caucion.estado;
        return;
    }
    if (userRole === 'Trader') {
      if (newStatus === 'Rechazada') {
        setCaucionToReject(caucion.id);
        setIsRejectionModalOpen(true);
        return;
      }
      if (newStatus === 'Hecha') {
        setMontoOperadoValue(formatNumberForDisplay(caucion.monto));
        setTasaOperadaValue(formatTasaForDisplay(caucion.tasa));
        setMontoOperadoModalState({ isOpen: true, caucion: caucion });
        return;
      }
    }
    const docRef = doc(db, "cauciones", caucion.id);
    const payload = { estado: newStatus };
    if (userRole === 'Trader' && newStatus === 'En Proceso') {
      payload.operador = userName;
    } else if (userRole === 'Trader' && newStatus === 'Pendiente') {
      payload.operador = '';
    }
    await updateDoc(docRef, payload);
  };

  const handleMontoOperadoSubmit = async () => {
    const { caucion } = montoOperadoModalState;
    if (!caucion) return;
    // Igual que el monto, el monto operado no admite decimales: se redondea hacia arriba.
    const montoFinal = Math.ceil(parseFloat(parseAmountForState(montoOperadoValue)) || 0);
    const tasaFinal = parseFloat(parseTasaForState(tasaOperadaValue)) || 0;
    const docRef = doc(db, "cauciones", caucion.id);
    await updateDoc(docRef, {
        estado: 'Hecha',
        operador: userName,
        montoOperado: montoFinal,
        tasaOperada: tasaFinal
    });
    setMontoOperadoModalState({ isOpen: false, caucion: null });
    setMontoOperadoValue('');
    setTasaOperadaValue('');
  };

  const handleRejectionSubmit = async () => {
    if (!rejectionComment.trim()) {
      alert("El comentario es obligatorio para rechazar una caución.");
      return;
    }
    const docRef = doc(db, "cauciones", caucionToReject);
    await updateDoc(docRef, { estado: 'Rechazada', comentarioTrader: rejectionComment, operador: userName });
    setIsRejectionModalOpen(false);
    setRejectionComment('');
  };

  const handleEditClick = (caucion) => {
    setEditingCaucion(caucion);
    setCaucionPrefill(null);
    setIsModalOpen(true);
  };

  const handleTasaUpdate = async (caucionId) => {
    const newTasa = parseFloat(parseTasaForState(editingTasaValue));
    if (isNaN(newTasa)) {
      console.error("Valor de tasa inválido");
      setEditingTasaId(null);
      return;
    }
    const docRef = doc(db, "cauciones", caucionId);
    try {
      await updateDoc(docRef, { tasa: newTasa });
    } catch (error) {
      console.error("Error al actualizar la tasa: ", error);
    } finally {
      setEditingTasaId(null);
    }
  };

  const getRowStyle = (estado) => {
    switch(estado) {
        case 'En Proceso': return { backgroundColor: styles.colors.yellow };
        case 'Hecha': return { backgroundColor: styles.colors.green };
        case 'Rechazada': return { backgroundColor: styles.colors.red };
        default: return {};
    }
  }

  const renderCommentCell = (comment, title) => {
    const MAX_LENGTH = 25;
    if (!comment) return '-';
    if (comment.length > MAX_LENGTH) {
      return (
        <span
          style={styles.clickableText}
          title="Ver comentario completo"
          onClick={() => onViewComment(title, comment)}
        >
          {comment.substring(0, MAX_LENGTH)}...
        </span>
      );
    }
    return comment;
  };

  const tableHeaders = [
    { key: 'persistentId', label: 'ID' },
    { key: 'actions', label: 'Acciones' },
    { key: 'createdAt', label: 'Fecha' },
    { key: 'horaCarga', label: 'Hora', sortable: false, narrow: true, tooltip: 'Hora de carga de la orden' },
    { key: 'operacion', label: 'Operación' },
    { key: 'nroComitente', label: 'Nro Comitente' }, { key: 'cliente', label: 'Cliente' },
    { key: 'saldoCliente', label: 'Saldo Cliente', sortable: false, tooltip: 'Saldo proyectado del cliente en la moneda de la caución' },
    { key: 'monto', label: 'Monto' }, { key: 'montoOperado', label: 'Monto Operado' },
    { key: 'dias', label: 'Días' }, { key: 'vencimiento', label: 'Vencimiento' },
    { key: 'tasa', label: 'Tasa' }, { key: 'tasaOperada', label: 'Tasa Operada' }, { key: 'moneda', label: 'Moneda' }, { key: 'mercado', label: 'Mercado' },
    { key: 'comercial', label: 'Comercial' }, { key: 'operador', label: 'Operador' }, { key: 'estado', label: 'Estado' },
    { key: 'comentarioComercial', label: 'Com. Comercial' },
    { key: 'comentarioTrader', label: 'Com. Trader' },
    { key: 'parciales', label: 'Parciales' }
  ];

  return (
    <div>
      <div className="axon-scroll-x" style={subTabContainerStyle}>
        <button
          type="button"
          onClick={() => setActiveSubTab('planilla')}
          style={activeSubTab === 'planilla' ? { ...subTabButtonStyle, ...subTabButtonActiveStyle } : subTabButtonStyle}
        >
          Planilla de Cauciones
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('pases')}
          style={activeSubTab === 'pases' ? { ...subTabButtonStyle, ...subTabButtonActiveStyle } : subTabButtonStyle}
        >
          Planilla de Pases
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('vencimientos')}
          style={activeSubTab === 'vencimientos' ? { ...subTabButtonStyle, ...subTabButtonActiveStyle } : subTabButtonStyle}
        >
          Vencimientos
        </button>
        {!esModoProductor && (
          <button
            type="button"
            onClick={() => setActiveSubTab('conciliacion')}
            style={activeSubTab === 'conciliacion' ? { ...subTabButtonStyle, ...subTabButtonActiveStyle } : subTabButtonStyle}
          >
            Conciliación
          </button>
        )}
      </div>

      {activeSubTab === 'planilla' && (
      <>
      <MobileCollapsibleSection title="Indicadores" summary="Tomadoras y colocadoras">
      {MONEDA_BUCKETS.map(bucket => (
        <div key={bucket} style={{marginBottom: '2rem'}}>
          <h2 style={{...styles.logo, fontSize: '1.25rem', marginBottom: '1rem', borderBottom: '1px solid #E5E7EB', paddingBottom: '0.5rem'}}>
            Indicadores ({MONEDA_TITULO[bucket]})
          </h2>
          <div className="axon-grid-2" style={styles.kpiContainer}>
              <IndicatorCard title="Tomadoras Totales" value={kpiTotales[bucket].tomadorasTotales} currency={MONEDA_MOVIMIENTO[bucket]} />
              <IndicatorCard title="Tomadoras Operadas" value={kpiTotales[bucket].tomadorasOperadas} currency={MONEDA_MOVIMIENTO[bucket]} />
              <IndicatorCard title="Colocadoras Totales" value={kpiTotales[bucket].colocadorasTotales} currency={MONEDA_MOVIMIENTO[bucket]} />
              <IndicatorCard title="Colocadoras Operadas" value={kpiTotales[bucket].colocadorasOperadas} currency={MONEDA_MOVIMIENTO[bucket]} />
          </div>
        </div>
      ))}
      </MobileCollapsibleSection>

      {!tieneSnapshotInicialHoy && (
        <div style={{
          backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E',
          padding: '0.75rem 1rem', borderRadius: '0.375rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem'
        }} className="axon-wrap">
          <span style={{ fontWeight: 600 }}>
            Aún no se cargó el snapshot del custodio para hoy. Los saldos de los clientes no están disponibles.
          </span>
          {onNavigateToSaldos && (
            <button
              onClick={onNavigateToSaldos}
              style={{ ...styles.button, backgroundColor: '#92400E', padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}
            >
              Ir a Control de Saldos
            </button>
          )}
        </div>
      )}

      {tieneSnapshotInicialHoy && hayDeudores && (
        <div style={{
          backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B',
          padding: '0.75rem 1rem', borderRadius: '0.375rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600 }}>
            Clientes con saldo deudor sin línea precargada:
            {monedasConDeudores.map((bucket, i) => (
              <span key={bucket}>
                {` ${deudoresPorMoneda[MONEDA_MOVIMIENTO[bucket]].length} en ${MONEDA_MOVIMIENTO[bucket]}`}
                {i < monedasConDeudores.length - 1 && ','}
              </span>
            ))}
          </span>
          <div className="axon-wrap" style={{ display: 'flex', gap: '0.5rem' }}>
            {monedasConDeudores.map(bucket => (
              <button
                key={bucket}
                onClick={() => setDeudoresPanel({ isOpen: true, moneda: MONEDA_MOVIMIENTO[bucket] })}
                style={{ ...styles.button, backgroundColor: '#991B1B', padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}
              >
                Ver {MONEDA_TITULO[bucket]} ({deudoresPorMoneda[MONEDA_MOVIMIENTO[bucket]].length})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sección de filtros */}
      <div style={filterContainerStyle}>
        <div style={{ ...filterHeaderStyle, gap: '0.75rem', ...(filtersPanel.isOpen ? {} : { borderBottom: 'none' }) }}>
          <CollapsibleHeader isMobile={filtersPanel.isMobile} isOpen={filtersPanel.isOpen} onToggle={filtersPanel.toggle} style={filterHeaderLeftStyle}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            <h3 style={filterTitleStyle}>Filtros</h3>
            {hasActiveFilters && (
              <span style={filterBadgeStyle}>{Object.keys(filters).length}</span>
            )}
          </CollapsibleHeader>
          {hasActiveFilters && (
            <button style={clearFiltersButtonStyle} onClick={clearFilters}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              Limpiar filtros
            </button>
          )}
        </div>
        {filtersPanel.isOpen && <div className="axon-grid-2" style={filterGridStyle}>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Cliente</label>
            <input style={{ ...filterInputStyle, ...(filters.cliente ? filterInputActiveStyle : {}) }} type="text" placeholder="Nombre..." value={filters.cliente || ''} onChange={e => handleFilterChange('cliente', e.target.value)} />
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Nro Comitente</label>
            <input style={{ ...filterInputStyle, ...(filters.nroComitente ? filterInputActiveStyle : {}) }} type="text" placeholder="Ej: 568" value={filters.nroComitente || ''} onChange={e => handleFilterChange('nroComitente', e.target.value)} />
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Operación</label>
            <select style={{ ...filterSelectFieldStyle, ...(filters.operacion ? filterSelectFieldActiveStyle : {}) }} value={filters.operacion || ''} onChange={e => handleFilterChange('operacion', e.target.value)}>
              <option value="">Todas</option>
              {formOptions.operacion.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Moneda</label>
            <select style={{ ...filterSelectFieldStyle, ...(filters.moneda ? filterSelectFieldActiveStyle : {}) }} value={filters.moneda || ''} onChange={e => handleFilterChange('moneda', e.target.value)}>
              <option value="">Todas</option>
              {formOptions.moneda.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Mercado</label>
            <select style={{ ...filterSelectFieldStyle, ...(filters.mercado ? filterSelectFieldActiveStyle : {}) }} value={filters.mercado || ''} onChange={e => handleFilterChange('mercado', e.target.value)}>
              <option value="">Todos</option>
              {formOptions.mercado.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Comercial</label>
            <select style={{ ...filterSelectFieldStyle, ...(filters.comercial ? filterSelectFieldActiveStyle : {}) }} value={filters.comercial || ''} onChange={e => handleFilterChange('comercial', e.target.value)}>
              <option value="">Todos</option>
              {uniqueComerciales.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Operador</label>
            <select style={{ ...filterSelectFieldStyle, ...(filters.operador ? filterSelectFieldActiveStyle : {}) }} value={filters.operador || ''} onChange={e => handleFilterChange('operador', e.target.value)}>
              <option value="">Todos</option>
              {uniqueOperadores.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Estado</label>
            <select style={{ ...filterSelectFieldStyle, ...(filters.estado ? filterSelectFieldActiveStyle : {}) }} value={filters.estado || ''} onChange={e => handleFilterChange('estado', e.target.value)}>
              <option value="">Todos</option>
              {formOptions.estado.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{...styles.logo, fontSize: '1.5rem', margin: 0}}>Planilla de Cauciones</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6B7281' }}>Ordenar:</span>
            <div style={{ display: 'inline-flex', border: '1px solid #D1D5DB', borderRadius: '0.375rem', overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setSortConfig({ key: 'urgencia', direction: 'descending' })}
                title="Pendientes con mayor saldo deudor (tomadoras) o acreedor (colocadoras) primero"
                style={{
                  border: 'none',
                  padding: '0.45rem 0.9rem',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: sortConfig.key === 'urgencia' ? styles.colors.primary : '#FFFFFF',
                  color: sortConfig.key === 'urgencia' ? '#FFFFFF' : '#374151',
                  borderRight: '1px solid #D1D5DB',
                  transition: 'background-color 0.15s',
                }}
              >
                Mayor deuda 1°
              </button>
              <button
                type="button"
                onClick={() => setSortConfig({ key: 'createdAt', direction: 'descending' })}
                title="Cauciones cargadas más recientemente primero"
                style={{
                  border: 'none',
                  padding: '0.45rem 0.9rem',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: sortConfig.key === 'createdAt' && sortConfig.direction === 'descending' ? styles.colors.primary : '#FFFFFF',
                  color: sortConfig.key === 'createdAt' && sortConfig.direction === 'descending' ? '#FFFFFF' : '#374151',
                  transition: 'background-color 0.15s',
                }}
              >
                Más recientes 1°
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {userRole === 'Trader' && (
              <button
                style={{ ...styles.button, backgroundColor: '#4F46E5' }}
                onClick={() => setIsImportModalOpen(true)}
                title="Subir el listado de órdenes para imputar las cauciones operadas"
              >
                Importar operaciones
              </button>
            )}
            <button className="axon-fab" style={styles.button} onClick={() => { setEditingCaucion(null); setCaucionPrefill(null); setIsModalOpen(true); }}>
              + Agregar Caución
            </button>
          </div>
        </div>
      </div>
      <TopScrollbar scrollRef={topRef} onScroll={onTopScroll} width={contentWidth} />

      <div style={styles.tableContainer} ref={contentRef} onScroll={onContentScroll}>
        {isLoading ? <p style={{textAlign: 'center', padding: '2rem'}}>Cargando cauciones...</p> :
        <table ref={stackedTable.ref} className={stackedTable.className} style={styles.table}>
          <thead>
            <tr>
              {tableHeaders.map(header => {
                const isClickable = header.key !== 'actions' && header.sortable !== false;
                return (
                  <th
                    key={header.key}
                    style={{ ...styles.th, ...(header.narrow ? styles.narrowCol : {}), cursor: isClickable ? 'pointer' : 'default' }}
                    onClick={() => isClickable && requestSort(header.key)}
                    title={header.tooltip || undefined}
                  >
                    {header.label}
                    {isClickable && sortConfig.key === header.key && <SortIcon direction={sortConfig.direction} />}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredCauciones.map((caucion) => {
              const rowStyle = getRowStyle(caucion.estado);
              const canEdit = ['Comercial', 'Trader', 'MiddleOffice'].includes(userRole) && caucion.estado === 'Pendiente';
              // Middle office elimina las que cargó él en apoyo de un comercial.
              const canDelete = caucion.estado === 'Pendiente'
                && ((userRole === 'Comercial' && caucion.comercial === userName)
                  || (userRole === 'MiddleOffice' && caucion.creadoPorId === userId));
              const canEditTasa = userRole === 'Trader' && caucion.estado === 'Hecha';
              const canLoadParcial = userRole === 'Trader' && caucion.estado === 'En Proceso' && caucion.operador === userName;
              const saldoFila = getSaldoComitente(caucion.nroComitente, caucion.moneda);

              return (
              <tr key={caucion.id} style={rowStyle}>
                <td style={styles.td}>{caucion.persistentId}</td>
                <td style={{...styles.td, textAlign: 'center'}}>
                  <button style={{...styles.iconButton, color: canEdit ? styles.primary : '#D1D5DB'}} onClick={() => handleEditClick(caucion)} disabled={!canEdit} title={canEdit ? "Editar" : "No tiene permisos"}><PencilIcon /></button>
                  <button style={{...styles.iconButton, color: canDelete ? styles.redText : '#D1D5DB'}} onClick={() => handleDelete(caucion)} disabled={!canDelete} title={canDelete ? "Eliminar" : "No tiene permisos"}><TrashIcon /></button>
                </td>
                <td style={styles.td}>{caucion.createdAt?.toDate().toLocaleDateString('es-AR')}</td>
                <td style={{ ...styles.td, ...styles.narrowCol }}>{formatHoraForDisplay(caucion.createdAt)}</td>
                <td style={styles.td}><OperacionBadge operacion={caucion.operacion} estado={caucion.estado} /></td>
                <td style={styles.td}>{formatComitenteForDisplay(caucion.nroComitente)}</td>
                <td style={styles.td}>{renderCommentCell(caucion.cliente, 'Cliente')}</td>
                <td style={{...styles.td, textAlign: 'right'}}>
                  {saldoFila ? (
                    <SaldoClienteBadge
                      saldoReal={saldoFila.saldoReal}
                      saldoProyectado={saldoFila.saldoProyectado}
                      moneda={caucion.moneda}
                      tieneSnapshot={saldoFila.tieneSnapshot}
                      compact
                    />
                  ) : '-'}
                </td>
                <td style={{...styles.td, textAlign: 'right'}}>{formatCurrency(caucion.monto, caucion.moneda)}</td>
                <td style={{...styles.td, textAlign: 'right'}}>{caucion.montoOperado ? formatCurrency(caucion.montoOperado, caucion.moneda) : '-'}</td>
                <td style={{...styles.td, textAlign: 'right'}}>{caucion.dias}</td>
                <td style={styles.td}>{calculateVencimiento(caucion.createdAt, caucion.dias)?.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                <td style={{...styles.td, textAlign: 'right', cursor: canEditTasa ? 'pointer' : 'default', backgroundColor: editingTasaId === caucion.id ? '#F9FAFB' : 'transparent'}} onClick={() => { if (canEditTasa) { setEditingTasaId(caucion.id); setEditingTasaValue(formatNumberForInput(caucion.tasa)); } }}>
                  {editingTasaId === caucion.id ? ( <input type="text" inputMode="decimal" value={editingTasaValue} onChange={(e) => setEditingTasaValue(e.target.value)} onBlur={() => handleTasaUpdate(caucion.id)} onKeyDown={(e) => { if (e.key === 'Enter') handleTasaUpdate(caucion.id); if (e.key === 'Escape') setEditingTasaId(null); }} autoFocus style={{ ...styles.input, textAlign: 'right', height: 'auto', padding: '0.25rem', width: '80px' }} /> ) : ( formatTasaForDisplay(caucion.tasa) )}
                </td>
                <td style={{...styles.td, textAlign: 'right'}}>{caucion.tasaOperada ? formatTasaForDisplay(caucion.tasaOperada) : '-'}</td>
                <td style={styles.td}>{caucion.moneda}</td>
                <td style={styles.td}>{caucion.mercado}</td>
                <td style={styles.td}>{caucion.comercial}</td>
                <td style={styles.td}>{caucion.operador}</td>
                <td style={styles.td}>{userRole === 'Trader' ? (<select data-id={caucion.id} value={caucion.estado} onChange={(e) => handleStatusChange(caucion, e.target.value)} style={{...styles.select, marginTop: 0, padding: '0.25rem', backgroundColor: 'rgba(255,255,255,0.6)'}}>{formOptions.estado.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select>) : ( caucion.estado )}</td>
                <td style={styles.td}>{renderCommentCell(caucion.comentarioComercial, 'Comentario Comercial')}</td>
                <td style={styles.td}>{renderCommentCell(caucion.comentarioTrader, 'Comentario del Trader')}</td>
                <td style={{...styles.td, textAlign: 'center'}}>
                  {canLoadParcial ? (
                    <button
                      style={{...styles.button, padding: '0.25rem 0.5rem', fontSize: '0.75rem'}}
                      onClick={() => setParcialesModalState({ isOpen: true, caucion: caucion })}
                    >
                      Parciales
                    </button>
                  ) : '-'}
                </td>
              </tr>
            )})}
          </tbody>
        </table>
        }
      </div>
      </>
      )}

      {activeSubTab === 'pases' && (
        <PasesPlanilla
          pases={pases}
          isLoading={isPasesLoading}
          userRole={userRole}
          userName={userName}
          userId={userId}
          clientesDb={clientesDb}
          especies={especies}
          onViewComment={onViewComment}
          filtroMisClientes={filtroMisClientes}
        />
      )}

      {activeSubTab === 'vencimientos' && (
        <>
          {!esModoProductor && <CierreHealthBanner />}
          <VencimientosTab vencimientos={vencimientosMerged} isLoading={isVencimientosLoading} />
        </>
      )}

      {activeSubTab === 'conciliacion' && importFeedback && (
        <div style={{
          backgroundColor: '#D1FAE5', border: '1px solid #6EE7B7', color: '#065F46',
          padding: '0.75rem 1rem', borderRadius: '0.375rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
        }}>
          <span style={{ fontWeight: 600 }}>
            Importación de {importFeedback.mercado} lista: {importFeedback.partidasGuardadas} partida(s) procesada(s)
            {' '}y {importFeedback.caucionesActualizadas} caución/es actualizada(s)
            {importFeedback.parcialesReasignados > 0 && `, ${importFeedback.parcialesReasignados} reasignada(s)`}.
          </span>
          <button
            onClick={() => setImportFeedback(null)}
            style={{ ...styles.button, backgroundColor: '#065F46', padding: '0.35rem 0.8rem', fontSize: '0.8rem' }}
          >
            Entendido
          </button>
        </div>
      )}

      {activeSubTab === 'conciliacion' && !esModoProductor && (
        <ConciliacionCaucionesPanel
          cauciones={cauciones}
          clientesDb={clientesDb}
          userRole={userRole}
          onImportar={() => setIsImportModalOpen(true)}
        />
      )}

      <ImportarCaucionesModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportado={(resumen) => {
          setActiveSubTab('conciliacion');
          setImportFeedback(resumen);
        }}
        cauciones={cauciones}
        clientesDb={clientesDb}
        comitentePorCuit={comitentePorCuit}
        userName={userName}
        userId={userId}
      />

      <ParcialesModal
        isOpen={parcialesModalState.isOpen}
        onClose={() => setParcialesModalState({ isOpen: false, caucion: null })}
        caucion={parcialesModalState.caucion}
        userName={userName}
      />

      <CaucionFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingCaucion(null); setCaucionPrefill(null); }}
        clientesDb={clientesDb}
        userRole={userRole}
        userName={userName}
        userId={userId}
        editingCaucion={editingCaucion}
        prefill={caucionPrefill}
      />

      <Modal
        isOpen={isRejectionModalOpen}
        title="Motivo de Rechazo"
        maxWidth="500px"
        footer={<>
          <button onClick={() => setIsRejectionModalOpen(false)} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cancelar</button>
          <button onClick={handleRejectionSubmit} style={fieldStyles.button}>Confirmar Rechazo</button>
        </>}
      >
        <p style={{ margin: '0 0 1rem 0', color: styles.colors.secondary }}>Por favor, ingrese el motivo por el cual se rechaza esta operación.</p>
        <textarea value={rejectionComment} onChange={(e) => setRejectionComment(e.target.value)} style={{ ...fieldStyles.textarea, minHeight: '100px' }} autoFocus></textarea>
      </Modal>

      {deudoresPanel.isOpen && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modalContent, maxWidth: '720px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: styles.colors.primary, margin: 0 }}>
                Clientes deudores sin línea precargada ({deudoresPanel.moneda})
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {MONEDA_BUCKETS
                  .filter(bucket => MONEDA_MOVIMIENTO[bucket] !== deudoresPanel.moneda)
                  .map(bucket => (
                    <button
                      key={bucket}
                      onClick={() => setDeudoresPanel(prev => ({ ...prev, moneda: MONEDA_MOVIMIENTO[bucket] }))}
                      style={{ ...styles.button, backgroundColor: '#E5E7EB', color: '#374151', padding: '0.35rem 0.8rem', fontSize: '0.8rem' }}
                      title="Cambiar moneda"
                    >
                      Ver {MONEDA_TITULO[bucket]} ({deudoresPorMoneda[MONEDA_MOVIMIENTO[bucket]].length})
                    </button>
                  ))}
              </div>
            </div>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#6B7281' }}>
              Estos clientes amanecen con saldo deudor y no tienen línea de caución cargada hoy. Hacé click en "Crear" para precargar una tomadora.
            </p>
            <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: '0.375rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F9FAFB' }}>
                  <tr>
                    <th style={{ ...styles.th, padding: '0.5rem 0.75rem', textAlign: 'left' }}>Nro Comitente</th>
                    <th style={{ ...styles.th, padding: '0.5rem 0.75rem', textAlign: 'left' }}>Cliente</th>
                    <th style={{ ...styles.th, padding: '0.5rem 0.75rem', textAlign: 'right' }}>Saldo proyectado</th>
                    <th style={{ ...styles.th, padding: '0.5rem 0.75rem', textAlign: 'center' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {(deudoresPorMoneda[deudoresPanel.moneda] || []).map(d => (
                    <tr key={d.nroComitente}>
                      <td style={{ ...styles.td, padding: '0.5rem 0.75rem', textAlign: 'left' }}>{formatComitenteForDisplay(d.nroComitente)}</td>
                      <td style={{ ...styles.td, padding: '0.5rem 0.75rem', textAlign: 'left' }}>{d.nombre}</td>
                      <td style={{ ...styles.td, padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                        <SaldoClienteBadge
                          saldoReal={d.saldoReal}
                          saldoProyectado={d.saldoProyectado}
                          moneda={deudoresPanel.moneda}
                          tieneSnapshot={d.tieneSnapshot}
                          compact
                        />
                      </td>
                      <td style={{ ...styles.td, padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                        <button
                          onClick={() => handleCrearParaDeudor(d, deudoresPanel.moneda)}
                          style={{ ...styles.button, padding: '0.35rem 0.8rem', fontSize: '0.8rem' }}
                        >
                          + Crear
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(deudoresPorMoneda[deudoresPanel.moneda] || []).length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ ...styles.td, padding: '1rem', textAlign: 'center', fontStyle: 'italic', color: '#465163' }}>
                        No hay deudores sin precarga en {deudoresPanel.moneda}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeudoresPanel({ isOpen: false, moneda: deudoresPanel.moneda })}
                style={{ ...styles.button, backgroundColor: styles.colors.lightGray, color: styles.colors.secondary }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {montoOperadoModalState.isOpen && (
        <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: styles.colors.primary}}>Confirmar Operación</h3>
                <p style={{margin: '1rem 0', color: styles.colors.secondary}}>Ingrese el monto final operado y la tasa operada para la caución de <span style={{fontWeight: 'bold'}}>{montoOperadoModalState.caucion?.cliente}</span>.</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                  <div>
                    <label style={styles.label}>Monto Operado</label>
                    <input type="text" inputMode="decimal" value={montoOperadoValue} onChange={(e) => setMontoOperadoValue(e.target.value)} onFocus={(e) => setMontoOperadoValue(formatNumberForInput(e.target.value))} onBlur={(e) => { const rounded = Math.ceil(parseFloat(parseAmountForState(e.target.value)) || 0); setMontoOperadoValue(formatNumberForDisplay(String(rounded))); }} style={styles.input} autoFocus/>
                  </div>
                  <div>
                    <label style={styles.label}>Tasa Operada (%)</label>
                    <input type="text" inputMode="decimal" value={tasaOperadaValue} onChange={(e) => setTasaOperadaValue(e.target.value)} onFocus={(e) => setTasaOperadaValue(formatNumberForInput(e.target.value))} onBlur={(e) => { const parsed = parseTasaForState(e.target.value); setTasaOperadaValue(formatTasaForDisplay(parsed)); }} style={styles.input} />
                  </div>
                </div>
                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button onClick={() => { setMontoOperadoModalState({ isOpen: false, caucion: null }); setMontoOperadoValue(''); setTasaOperadaValue(''); }} style={{ ...styles.button, backgroundColor: styles.colors.lightGray, color: styles.colors.secondary }}>Cancelar</button>
                    <button onClick={handleMontoOperadoSubmit} style={styles.button}>Confirmar y Marcar como Hecha</button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};


export default CaucionesTab;
